var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Keycloak from 'keycloak-js';
import config from '../config';
/*
 * Mock provider used to allow testing without spinning up an auth server.
*/
const mockAuthProvider = {
    authenticated: true,
    getToken: () => Promise.resolve('ThisIsAMockToken'),
    login: () => Promise.resolve(),
    logout: () => Promise.resolve()
};
function clearStorage() {
    // Clear the session storage set by the Control Centre
    sessionStorage.clear();
    // Clear the legacy local storage set by the Control Centre (this can be removed once everything is released)
    localStorage.setItem('isUserSignedIn', 'false');
    localStorage.removeItem('userName');
    localStorage.removeItem('userId');
    localStorage.removeItem('userRoleAdministrator');
    localStorage.removeItem('userRoleSafetyManager');
    localStorage.removeItem('userRoleSupervisor');
    localStorage.removeItem('userRoleOperator');
    localStorage.removeItem('applicationState');
    localStorage.removeItem('_acls');
    localStorage.removeItem('administratesEntityIds');
    localStorage.removeItem('safetyManagesEntityIds');
    localStorage.removeItem('supervisesEntityIds');
    localStorage.removeItem('operatesInEntityIds');
    localStorage.removeItem('customerName');
    localStorage.removeItem('customerAutomatedMonitoringEnabled');
    localStorage.removeItem('$LoopBack$accessTokenId');
    localStorage.removeItem('$LoopBack$currentUserId');
    localStorage.removeItem('$LoopBack$rememberMe');
}
/*
 * Wraps keycloak to provide a consistant promise based interface
 */
function keycloakFactory() {
    const keycloak = Keycloak({
        clientId: config.auth.clientId,
        realm: config.auth.realm,
        url: config.auth.url
    });
    let authenticated = false;
    function init() {
        return new Promise((resolve, reject) => {
            keycloak.init({ onLoad: 'login-required' })
                .success((keycloakAuth) => {
                resolve(keycloakAuth);
            })
                .error(reject);
        });
    }
    function getToken() {
        return new Promise((resolve, reject) => {
            if (!authenticated) {
                return reject(Error('Cannot obtain token user is not authenticated'));
            }
            keycloak.updateToken(config.auth.accessTokenValidity)
                .success(() => {
                resolve(keycloak.token);
            })
                .error(reject);
        });
    }
    function login() {
        return __awaiter(this, void 0, void 0, function* () {
            const keycloakAuth = yield init();
            if (keycloakAuth) {
                authenticated = true;
                return;
            }
            return new Promise((resolve, reject) => {
                keycloak.login()
                    .success(() => {
                    authenticated = true;
                    resolve();
                })
                    .error(reject);
            });
        });
    }
    function logout() {
        clearStorage();
        return new Promise((resolve, reject) => {
            keycloak.logout()
                .success(() => {
                authenticated = false;
                resolve();
            })
                .error(reject);
        });
    }
    return {
        authenticated,
        getToken,
        login,
        logout
    };
}
function cognitoAuthProvider() {
    let authenticated = false;
    function refreshCognitoToken(refreshToken) {
        return __awaiter(this, void 0, void 0, function* () {
            const userPoolName = JSON.parse(sessionStorage.getItem('user_pool_name'));
            const params = new URLSearchParams({
                grant_type: 'refresh_token',
                client_id: JSON.parse(sessionStorage.getItem('client_id')),
                refresh_token: refreshToken
            });
            const response = yield fetch(`https://${userPoolName}.${config.auth.cognitoBaseDomain}/oauth2/token`, {
                method: 'POST',
                body: params.toString(),
                headers: new Headers({
                    'Content-Type': 'application/x-www-form-urlencoded'
                })
            });
            const responseBody = yield response.json();
            sessionStorage.setItem('access_token', JSON.stringify(responseBody.access_token));
            sessionStorage.setItem('id_token', JSON.stringify(responseBody.id_token));
            if (responseBody.refresh_token) {
                sessionStorage.setItem('refresh_token', JSON.stringify(responseBody.refresh_token));
            }
            return {
                accessToken: responseBody.access_token,
                idToken: responseBody.id_token
            };
        });
    }
    function getToken() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!authenticated) {
                throw new Error('Cannot obtain token user is not authenticated');
            }
            const token = JSON.parse(sessionStorage.getItem('id_token'));
            const payload = decode(token);
            if (payload.exp - Date.now() / 1000 < config.auth.accessTokenValidity) {
                const { idToken } = yield refreshCognitoToken(JSON.parse(sessionStorage.getItem('refresh_token')));
                return idToken;
            }
            return token;
        });
    }
    function login() {
        return __awaiter(this, void 0, void 0, function* () {
            // We don't need to be able to log in here because you'd need a custom callback url for every frontend app to
            // be able to use it anyway. Just check the token exists and set the flag
            const token = JSON.parse(sessionStorage.getItem('id_token'));
            if (!token) {
                throw new Error('Unauthorised');
            }
            authenticated = true;
        });
    }
    function logout() {
        return __awaiter(this, void 0, void 0, function* () {
            const clientId = JSON.parse(sessionStorage.getItem('client_id'));
            const userPoolName = JSON.parse(sessionStorage.getItem('user_pool_name'));
            clearStorage();
            const redirectUri = encodeURIComponent(window.location.origin + '/');
            window.location.href =
                `https://${userPoolName}.${config.auth.cognitoBaseDomain}/logout?client_id=${clientId}&logout_uri=${redirectUri}`;
        });
    }
    return {
        authenticated,
        getToken,
        login,
        logout
    };
}
function oidcAuthProvider() {
    let authenticated = false;
    function refreshOidcToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const refreshTokenUri = `${config.auth.authGatewayUrl}/refresh-token/oidc`;
            const body = {
                email: JSON.parse(sessionStorage.getItem('email')),
                refreshToken: JSON.parse(sessionStorage.getItem('refresh_token'))
            };
            const response = yield fetch(refreshTokenUri, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            if (!response.ok) {
                throw new Error('Failed to refresh OIDC token');
            }
            const responseBody = yield response.json();
            sessionStorage.setItem('access_token', JSON.stringify(responseBody.access_token));
            sessionStorage.setItem('id_token', JSON.stringify(responseBody.id_token));
            if (responseBody.refresh_token) {
                sessionStorage.setItem('refresh_token', JSON.stringify(responseBody.refresh_token));
            }
            return {
                accessToken: responseBody.access_token,
                idToken: responseBody.id_token
            };
        });
    }
    function getToken() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!authenticated) {
                throw new Error('Cannot obtain token user is not authenticated');
            }
            const idToken = JSON.parse(sessionStorage.getItem('id_token'));
            if (!idToken) {
                throw new Error('ID token not found in session storage');
            }
            const payload = decode(idToken);
            if (payload.exp - Date.now() / 1000 < config.auth.accessTokenValidity) {
                const { idToken: newIdToken } = yield refreshOidcToken();
                return newIdToken;
            }
            return idToken;
        });
    }
    function login() {
        return __awaiter(this, void 0, void 0, function* () {
            if (hasAuthGatewayOidcTokens()) {
                authenticated = true;
                return;
            }
            throw new Error('Unauthorised');
        });
    }
    function logout() {
        return __awaiter(this, void 0, void 0, function* () {
            const logoutEndpoint = JSON.parse(sessionStorage.getItem('auth_gateway_oidc_logout_uri'));
            clearStorage();
            if (logoutEndpoint) {
                window.location.href = logoutEndpoint;
            }
        });
    }
    return {
        authenticated,
        getToken,
        login,
        logout
    };
}
/*
 * Factory method to allow runtime configurable authentication providers
 */
export function authFactory(authProviderName) {
    switch (authProviderName) {
        case 'MOCK':
            return mockAuthProvider;
        case 'KEYCLOAK':
            return keycloakFactory();
        case 'COGNITO':
            return cognitoAuthProvider();
        case 'OIDC':
            return oidcAuthProvider();
        default:
            throw new Error(`Authentication failure ${authProviderName} is not a valid provider`);
    }
}
/**
 * @return True if the user has Cognito tokens in the session storage; false otherwise
 */
export function hasCognitoTokens() {
    return !!sessionStorage.getItem('id_token')
        && !!sessionStorage.getItem('access_token')
        && !!sessionStorage.getItem('refresh_token');
}
/**
 * @returns True if user has OIDC enabled and has tokens in session storage; false otherwise
 */
export function hasAuthGatewayOidcTokens() {
    const idToken = sessionStorage.getItem('id_token');
    const accessToken = sessionStorage.getItem('access_token');
    const refreshToken = sessionStorage.getItem('refresh_token');
    const isOidcEnabled = sessionStorage.getItem('auth_gateway_oidc_enabled');
    return !!idToken && !!accessToken && !!refreshToken && isOidcEnabled === 'true';
}
/**
 * Decodes a JWT payload. Borrowed from keycloak.js
 *
 * @param str The token to decode
 * @return {object} The decoded token payload
 */
function decode(str) {
    str = str.split('.')[1];
    str = str.replace(/-/g, '+');
    str = str.replace(/_/g, '/');
    switch (str.length % 4) {
        case 0:
            break;
        case 2:
            str += '==';
            break;
        case 3:
            str += '=';
            break;
        default:
            throw new Error('Invalid token');
    }
    str = decodeURIComponent(escape(atob(str)));
    return JSON.parse(str);
}
//# sourceMappingURL=authenticationFactory.js.map